<template>
  <div style="white-space: pre-line;">
    <div id="essay-writing" class="min-height">
      <b-container fluid class="background">
        <b-row class="pt-4 pb-2 title top-nav-row responsive-padding">
          <b-col class="px-0">
            <a class="strip top-back-link" @click="handleBack()">&larr; Back</a>
          </b-col>
        </b-row>
        <b-row class="responsive-padding breadcrumb-row">
          <breadcrumb :items="items"></breadcrumb>
        </b-row>
        <b-row>
          <b-col>
            <p class="instructions">
              Instructions: Self identify each section so we can assess and
              grade your essay.
            </p>
          </b-col>
        </b-row>
        <b-row class="white-container responsive-padding">
          <essayhead :prompt="exersize.prompt" :none="true"></essayhead>
          <div class="essay-area mx-1 mt-4">
            <div class="label-content-column">
              <b-alert
                v-if="authPrompt"
                show
                :variant="authPromptVariant"
                class="mt-3"
              >
                <div>{{ authPrompt }}</div>
                <div v-if="!isAuthenticated" class="mt-2 d-flex gap-2 flex-wrap">
                  <b-button size="sm" variant="outline-primary" @click="goToLogin">
                    Log in
                  </b-button>
                  <b-button size="sm" variant="primary" @click="goToSignup">
                    Sign up
                  </b-button>
                </div>
              </b-alert>
              <div
                class="fixed-toolbar-shell"
                :class="{ 'fixed-toolbar-shell--inactive': !selectionNode }"
              >
                <div class="fixed-toolbar-title">Before submitting, label each part of your essay</div>
                <p class="fixed-toolbar-description">


Highlight a section, then choose the label that best matches it (Contextualization, Thesis, Evidence, Analysis, or Conclusion).
Do this for every part so we can give accurate feedback.              </p>
                <div class="fixed-toolbar-row" role="toolbar" aria-label="Essay labeling toolbar">
                  <button
                    v-for="label in labelOptions"
                    :key="label.title"
                    type="button"
                    class="fixed-label-btn"
                    :disabled="!selectionNode"
                    :title="selectionNode ? `Label as ${label.title}` : 'Select text first'"
                    @click="handleFixedLabel(label)"
                  >
                    <span
                      class="fixed-label-dot"
                      :style="{ backgroundColor: label.color }"
                      aria-hidden="true"
                    ></span>
                    {{ label.title }}
                  </button>
                </div>
              </div>
              <div
                v-once
                ref="doc"
                id="essayArea"
                class="p-5 mt-5 position-relative labelled-contain"
                style="position: relative;"
                @mouseup="handleHighlight"
                @mousedown="checkHighlight"
                @click="handleClick"
              ></div>
              <!-- <b-row align-h="end" class="px-5 pt-5 pb-3">
              <b-button
                @click="parseUserSelections"
                id="purple"
                class="finish-button border-0 mb-2 fontlist purple-button"
                type="button"
                >Submit</b-button
              >
            </b-row> -->
              <b-row
                align-h="end"
                class="pt-5 pb-5 my-4 mt-5 mt-sm-0 mt-md-0 mt-lg-0 action-buttons-row"
              >
                <b-button
                  class="finish-button border-0 mb-1 fontist purple-button action-button action-button-submit"
                  @click="parseUserSelections"
                  :disabled="isSubmitting"
                  type="submit"
                  >{{ isSubmitting ? "Generating your feedback..." : "Submit Essay for Feedback" }}</b-button
                >
                <div v-if="isSubmitting" class="submit-note">
                  Submitting again is disabled while feedback is being generated.
                </div>
              </b-row>
              <section class="attempts-banner-near-submit mb-2">
                <feedback-attempts-banner
                  :remaining-attempts="remainingFeedbackAttempts"
                  :is-authenticated="isAuthenticated"
                  :is-pro-user="isProUser"
                />
              </section>
              <b-alert v-if="submissionError" show variant="danger" class="mt-2 mb-3">
                {{ submissionError }}
              </b-alert>
            </div>
              <feedback-generation-modal
                :visible="isSubmitting"
                :loading-step-message="loadingStepMessage"
                :show-long-wait-message="showLongWaitMessage"
              />
          </div>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";
import { pushRoute } from "@/router/navigation";
import essayhead from "../../components/writing_feature/LEQEssay/EssayHead.vue";
import breadcrumb from "../../components/writing_feature/BreadCrumb.vue";
import feedbackGenerationModal from "../../components/writing_feature/FeedbackGenerationModal.vue";
import feedbackAttemptsBanner from "../../components/writing_feature/FeedbackAttemptsBanner.vue";
import storeWriting from "../../store/writing";
import {
  getFeedback,
  isAuthRequiredError,
  isFeedbackLimitError
} from "@/services/getFeedback";
import { useSubscriptionAccess } from "@/composables/useSubscriptionAccess";
import { logDebugEvent } from "@/services/debugLogs";
import {
  createEssaySubmission,
  completeEssaySubmission,
  failEssaySubmission
} from "@/services/essaySubmissions";
export default {
  components: {
    essayhead,
    breadcrumb,
    feedbackGenerationModal,
    feedbackAttemptsBanner
  },
  setup() {
    const store = storeWriting;
    const isSubmitting = ref(false);
    const authPrompt = ref("");
    const authPromptVariant = ref("warning");
    const submissionError = ref("");
    const route = useRoute();
    const {
      isAuthenticated,
      isProUser,
      normalizedProfile,
      feedbackAccessState,
      refreshSubscriptionProfile
    } = useSubscriptionAccess();

    const remainingFeedbackAttempts = computed(() =>
      isProUser.value ? null : normalizedProfile.value.remainingFreeFeedback
    );

    const loadingStepMessages = [
      "Analyzing your thesis...",
      "Reviewing your evidence...",
      "Evaluating your reasoning...",
      "Putting together feedback..."
    ];
    const loadingStepIndex = ref(0);
    const showLongWaitMessage = ref(false);
    const loadingStepMessage = computed(
      () => loadingStepMessages[loadingStepIndex.value]
    );

    let loadingStepIntervalId = null;
    let longWaitTimeoutId = null;

    const stopSubmittingUi = () => {
      isSubmitting.value = false;
      showLongWaitMessage.value = false;

      if (loadingStepIntervalId) {
        clearInterval(loadingStepIntervalId);
        loadingStepIntervalId = null;
      }

      if (longWaitTimeoutId) {
        clearTimeout(longWaitTimeoutId);
        longWaitTimeoutId = null;
      }
    };

    const startSubmittingUi = () => {
      stopSubmittingUi();
      submissionError.value = "";
      isSubmitting.value = true;
      loadingStepIndex.value = 0;

      loadingStepIntervalId = setInterval(() => {
        loadingStepIndex.value =
          (loadingStepIndex.value + 1) % loadingStepMessages.length;
      }, 3500);

      longWaitTimeoutId = setTimeout(() => {
        showLongWaitMessage.value = true;
      }, 25000);
    };

    const goToLogin = () => {
      pushRoute("Login", {}, { redirect: route.fullPath });
    };

    const goToSignup = () => {
      pushRoute("Signup", {}, { redirect: route.fullPath });
    };

    //DOM MANIPULATION REFS
    const doc = ref(null);
    const range = ref(null);
    const preserve = ref(null);
    const selectionNode = ref(null);

    const selectionDebugEvents = [];
    let lastSelectionFingerprint = "";

    const getNodeMeta = node => {
      if (!node) {
        return null;
      }

      const parent = node.parentNode;
      return {
        nodeName: node.nodeName || null,
        nodeType: typeof node.nodeType === "number" ? node.nodeType : null,
        parentId: parent && parent.id ? parent.id : null,
        parentClass: parent && parent.className ? String(parent.className) : null
      };
    };

    const getSelectionState = preferredRange => {
      if (typeof window === "undefined" || !window.getSelection) {
        return {
          selectedText: "",
          selectedLength: 0,
          hasNativeSelectionRange: false,
          isCollapsed: true,
          range: null,
          anchorOffset: null,
          focusOffset: null,
          anchorNode: null,
          focusNode: null
        };
      }

      const selection = window.getSelection();
      const selectedText = (selection && selection.toString ? selection.toString() : "") || "";
      const range =
        preferredRange ||
        (selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null);

      return {
        selectedText,
        selectedLength: selectedText.trim().length,
        hasNativeSelectionRange: Boolean(selection && selection.rangeCount > 0),
        isCollapsed: selection ? selection.isCollapsed : true,
        range,
        anchorOffset: selection ? selection.anchorOffset : null,
        focusOffset: selection ? selection.focusOffset : null,
        anchorNode: selection ? getNodeMeta(selection.anchorNode) : null,
        focusNode: selection ? getNodeMeta(selection.focusNode) : null
      };
    };

    const persistedSelectionEvents = new Set([
      "DEBUG_SESSION_STARTED",
      "MOUSEUP_AFTER_SELECTION",
      "EMPTY_SELECTION_AFTER_MOUSEUP",
      "SELECTION_CREATED",
      "SELECTION_CLEARED",
      "LABEL_BUTTON_CLICKED",
      "LABEL_CLICKED",
      "LABEL_CLICKED_WITHOUT_SELECTION",
      "LABEL_APPLIED",
      "SELECTION_PENDING_ON_MOUSEDOWN",
      "SELECTION_SKIPPED_NON_ROOT"
    ]);

    const writeSelectionEvent = (eventType, details = {}, preferredRange = null) => {
      const state = getSelectionState(preferredRange);
      const range = state.range;

      const payload = {
        event: eventType,
        ts: new Date().toISOString(),
        selectedText: state.selectedText,
        selectedLength: state.selectedLength,
        hasNativeSelectionRange: state.hasNativeSelectionRange,
        isCollapsed: state.isCollapsed,
        rangeStartOffset: range ? range.startOffset : null,
        rangeEndOffset: range ? range.endOffset : null,
        rangeStartNode: range ? getNodeMeta(range.startContainer) : null,
        rangeEndNode: range ? getNodeMeta(range.endContainer) : null,
        anchorOffset: state.anchorOffset,
        focusOffset: state.focusOffset,
        anchorNode: state.anchorNode,
        focusNode: state.focusNode,
        hasSelectionNode: Boolean(selectionNode.value),
        selectionClass: selectionNode.value ? selectionNode.value.className : null,
        essayChildCount: doc.value ? doc.value.childNodes.length : null,
        ...details
      };

      selectionDebugEvents.push(payload);
      if (selectionDebugEvents.length > 600) {
        selectionDebugEvents.shift();
      }

      if (persistedSelectionEvents.has(eventType)) {
        logDebugEvent("label-selection", eventType, payload);
      }

      console.log(`[LabelSelectionDebug] ${JSON.stringify(payload)}`);
    };

    const handleSelectionChange = () => {
      const state = getSelectionState();
      const range = state.range;
      const fingerprint = [
        state.selectedText,
        state.isCollapsed,
        range ? range.startOffset : "",
        range ? range.endOffset : ""
      ].join("|");

      if (fingerprint === lastSelectionFingerprint) {
        return;
      }

      lastSelectionFingerprint = fingerprint;
      writeSelectionEvent("SELECTION_CHANGE", {
        reason: "document-selectionchange"
      });
    };

    const installSelectionDebugExports = () => {
      if (typeof window === "undefined") {
        return;
      }

      window.downloadLabelSelectionLog = () => {
        const lines = selectionDebugEvents.map(event => JSON.stringify(event));
        const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `label-selection-debug-${Date.now()}.log`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      };

      window.clearLabelSelectionLog = () => {
        selectionDebugEvents.length = 0;
        lastSelectionFingerprint = "";
      };
    };

    //USER WRITING REFS

    //NOTE! check if all sections are present in labels via className to prevent user manipulation with inspect element
    const parseUserSelections = async () => {
      if (isSubmitting.value) {
        return;
      }

      if (!isAuthenticated.value) {
        authPromptVariant.value = "warning";
        authPrompt.value =
          "Create a free account to save your essay and get feedback.";
        submissionError.value = "";
        return;
      }

      if (!feedbackAccessState.value.allowed) {
        authPromptVariant.value = "info";
        authPrompt.value =
          "No feedback attempts remaining. Upgrade to continue getting feedback.";
        submissionError.value = "";
        return;
      }

      authPrompt.value = "";
      submissionError.value = "";
      console.log("check");
      let thesis = "";
      let conclusion = "";
      let contextualization = "";
      const evidenceSelections = [];
      const analysisSelections = [];

      const safeNodeText = node =>
        node && typeof node.innerText === "string" ? node.innerText.trim() : "";

      doc.value.childNodes.forEach(elem => {
        const className = typeof elem.className === "string" ? elem.className : "";
        const classList = elem && elem.classList ? elem.classList : null;
        const text = safeNodeText(elem);
        if (!text) {
          return;
        }

        const isThesis = classList ? classList.contains("Thesis") : className === "Thesis";
        const isConclusion = classList
          ? classList.contains("Conclusion")
          : className === "Conclusion";
        const isContextualization = classList
          ? classList.contains("Contextualization")
          : className === "Contextualization";
        const isEvidence = classList
          ? classList.contains("Evidence")
          : className === "Evidence";
        const isAnalysis = classList
          ? classList.contains("Analysis")
          : className === "Analysis";

        if (isThesis) {
          console.log(text);
          thesis = thesis ? `${thesis}\n${text}` : text;
        } else if (isConclusion) {
          console.log(text);
          conclusion = conclusion ? `${conclusion}\n${text}` : text;
        } else if (isContextualization) {
          console.log(text);
          contextualization = contextualization ? `${contextualization}\n${text}` : text;
        } else if (isEvidence) {
          console.log(text);
          evidenceSelections.push(text);
        } else if (isAnalysis) {
          console.log(text);
          analysisSelections.push(text);
        }
      });

      // Build dynamic evidence/analysis pairs in selection order.
      const pairCount = Math.max(evidenceSelections.length, analysisSelections.length);
      const evidence = Array.from({ length: pairCount }, (_, index) => {
        const evidenceText = evidenceSelections[index] || "";
        const analysisText = analysisSelections[index] || "";
        return [
          evidenceText,
          String(analysisText).trim().length ? analysisText : evidenceText
        ];
      });

      const hasEssayContent =
        Boolean(String(contextualization || "").trim()) ||
        Boolean(String(thesis || "").trim()) ||
        Boolean(String(conclusion || "").trim()) ||
        evidenceSelections.some(item => Boolean(String(item || "").trim())) ||
        analysisSelections.some(item => Boolean(String(item || "").trim()));

      if (!hasEssayContent) {
        submissionError.value =
          "Highlight each part of your essay and use the toolbar above to label it (Contextualization, Thesis, Evidence, Analysis, Conclusion).";
        return;
      }

      // Keep explicit slots for compatibility with existing scoring/UI assumptions.
      const evidence1 = evidence[0] ? evidence[0][0] : "";
      const analysis1 = evidence[0] ? evidence[0][1] : "";
      const evidence2 = evidence[1] ? evidence[1][0] : "";
      const analysis2 = evidence[1] ? evidence[1][1] : "";

      const idea = {
        prompt_id: feedback.value.prompt_id, //ID to identify prompt
        id: store.getters.getUniqueId, //Unique ID assigned to new feedback obj (to be replaced by firestore id's)
        moduleVersion: "Expert", //For segregating beginner and advanced modules
        promptText:
          exersize.value && exersize.value.prompt
            ? exersize.value.prompt.prompt
            : "",

        //User input -- analysis is wrapped within the evidence obj
        contextualization: contextualization,
        evidence: evidence,
        conclusion: conclusion,
        thesis: thesis
      };
      console.log("checkpoint");
      store.dispatch("setUserInput", idea);
      store.dispatch("setModuleVersion", "Expert");

      const payload = {
        prompt: {
          id: feedback.value.prompt_id,
          text:
            exersize.value && exersize.value.prompt
              ? exersize.value.prompt.prompt
              : "",
          moduleVersion: "Expert"
        },
        writing: {
          contextualization: contextualization,
          thesis: thesis,
          evidence1: evidence1,
          analysis1: analysis1,
          evidence2: evidence2,
          analysis2: analysis2,
          evidenceSelections: evidenceSelections,
          analysisSelections: analysisSelections,
          evidencePairs: evidence,
          evidence: evidence,
          conclusion: conclusion,
          raw: feedback.value.content.raw
        }
      };

      let submissionId = null;
      startSubmittingUi();
      logDebugEvent("label-section", "submit-attempt", {
        moduleVersion: "Expert",
        promptId: feedback.value.prompt_id,
        isAuthenticated: isAuthenticated.value
      });

      // Save essay BEFORE calling feedback API so work is never lost
      try {
        submissionId = await createEssaySubmission({
          promptId: feedback.value.prompt_id,
          promptText: exersize.value && exersize.value.prompt ? exersize.value.prompt.prompt : null,
          essayText: feedback.value.content && feedback.value.content.raw ? feedback.value.content.raw.text : null,
          moduleVersion: "Expert"
        });
      } catch (saveErr) {
        console.warn("Could not pre-save essay submission:", saveErr);
      }

      try {
        console.log("Submitting payload:", payload);
        const result = await getFeedback(payload);
        console.log("FUNCTION RESULT:", result);
        store.dispatch("setBackendFeedback", {
          prompt_id: feedback.value.prompt_id,
          moduleVersion: "Expert",
          feedback: result
        });

        // Mark submission complete in Firestore
        if (submissionId) {
          try {
            await completeEssaySubmission(submissionId, {
              feedback: result,
              score: result?.overallScore ?? result?.score ?? null
            });
          } catch (updateErr) {
            console.warn("Could not mark submission complete:", updateErr);
          }
        }

        authPrompt.value = "";
        submissionError.value = "";
        await refreshSubscriptionProfile();

        logDebugEvent("label-section", "submit-success", {
          moduleVersion: "Expert",
          promptId: feedback.value.prompt_id,
          overallScore: result?.overallScore ?? null
        });

        console.log(
          "handled submit in labelled",
          store.state.feedback,
          store.state.completedPrompts
        );
        pushRoute("004", { id: store.state.uniqueId });
      } catch (error) {
        console.error("Failed to fetch backend feedback", error);
        logDebugEvent("label-section", "submit-failed", {
          code: error?.code || null,
          message: error?.message || null,
          isAuthenticated: isAuthenticated.value
        });

        // Mark submission as errored in Firestore
        if (submissionId) {
          try {
            await failEssaySubmission(submissionId, error?.message || "Feedback generation failed");
          } catch (updateErr) {
            console.warn("Could not mark submission as error:", updateErr);
          }
        }

        if (isAuthRequiredError(error)) {
          authPromptVariant.value = "warning";
          authPrompt.value =
            "Create a free account to save your essay and get feedback.";
          submissionError.value = "";
        } else if (isFeedbackLimitError(error)) {
          await refreshSubscriptionProfile();
          authPromptVariant.value = "info";
          authPrompt.value =
            "No feedback attempts remaining. Upgrade to continue getting feedback.";
          submissionError.value = "";
        } else {
          authPrompt.value = "";
          submissionError.value =
            "Something went wrong while generating feedback. Please try again. Your essay is still here, and you can resubmit.";
        }
      } finally {
        stopSubmittingUi();
      }
    };
    const handleClickLabel = label => {
      writeSelectionEvent("LABEL_CLICKED", {
        label: Array.isArray(label) ? label[1] : null,
        hasSelectionBeforeApply: Boolean(selectionNode.value)
      });

      if (selectionNode.value) {
        selectionNode.value.style.backgroundColor = label[0];
        selectionNode.value.style.color = "#1f2937";
        selectionNode.value.style.border = "1px solid rgba(31, 41, 55, 0.18)";
        selectionNode.value.style.borderRadius = "5px";
        selectionNode.value.className = `labelled-token ${label[1]}`;
        selectionNode.value = null;

        writeSelectionEvent("LABEL_APPLIED", {
          label: Array.isArray(label) ? label[1] : null
        });
      } else {
        writeSelectionEvent("LABEL_CLICKED_WITHOUT_SELECTION", {
          label: Array.isArray(label) ? label[1] : null
        });
      }
    };

    const labelOptions = [
      { title: "Contextualization", palette: "#CCEBA580", color: "#CCEBA5" },
      { title: "Thesis", palette: "#CAE2F180", color: "#CAE2F1" },
      { title: "Evidence", palette: "#DCBAE580", color: "#DCBAE5" },
      { title: "Analysis", palette: "#F5EBBB80", color: "#F5EBBB" },
      { title: "Conclusion", palette: "#FFC27880", color: "#FFC278" }
    ];

    const handleFixedLabel = label => {
      writeSelectionEvent("LABEL_BUTTON_CLICKED", {
        label: label && label.title ? label.title : null
      });
      handleClickLabel([label.palette, label.title]);
    };

    // Flag to prevent the click event (which always follows mouseup) from
    // immediately unwrapping the span that handleHighlight just created.
    let justHighlighted = false;

    const handleClick = event => {
      if (justHighlighted) {
        writeSelectionEvent("CLICK_IGNORED_JUST_HIGHLIGHTED", {
          targetId: event.target && event.target.id ? event.target.id : null
        });
        justHighlighted = false;
        return;
      }
      if (event.target.id === "background-editable") {
        writeSelectionEvent("SELECTION_CLEARED", {
          reason: "clicked-pending-highlight-wrapper",
          targetId: event.target.id
        });
        event.target.replaceWith(...event.target.childNodes);
        selectionNode.value = null;
      }
    };
    const recursion = elem => {
      var string = "";
      elem.childNodes.forEach(element => {
        //NOTE: depreciated, br not relevant
        if (element.nodeName === "BR") {
          string += "<br />";
          console.log("does it ever happen?");
        } else {
          if (element.childNodes.length > 0) {
            string += recursion(element);
          } else {
            string += element.textContent;
          }
        }
      });
      return string;
    };

    const removeHighlight = (reason = "selection-node-exists") => {
      if (selectionNode.value) {
        writeSelectionEvent("SELECTION_CLEARED", {
          reason
        });
        selectionNode.value.replaceWith(...selectionNode.value.childNodes);
        selectionNode.value = null;
      }
    };

    const checkHighlight = () => {
      if (selectionNode.value) {
        writeSelectionEvent("SELECTION_PENDING_ON_MOUSEDOWN", {
          reason: "preserve-until-new-selection-completes"
        });
      }
    };
    const preserveNewLines = elem => {
      var highlight = document.createElement("span");
      highlight.className = "labelled-token pending-label";
      highlight.style.position = "relative";
      highlight.style.borderRadius = "5px";
      highlight.style.userSelect = "none";
      highlight.style.backgroundColor = "#ffc7c2";
      highlight.style.border = "1px dashed rgba(185, 28, 28, 0.42)";
      highlight.style.padding = "0 1px";
      highlight.style.color = "#1f2937";
      highlight.style.boxShadow = "inset 0 0 0 1px rgba(185, 28, 28, 0.08)";
      highlight.id = "background-editable";
      highlight.innerHTML += recursion(elem);
      return highlight;
    };

    const handleHighlight = () => {
      const selection = window.getSelection();
      writeSelectionEvent("MOUSEUP_AFTER_SELECTION", {
        selectedTextPreview: (selection && selection.toString
          ? selection.toString().trim().slice(0, 40)
          : "")
      });

      if (!selection.toString().trim()) {
        console.log("Selected nothing else ");
        writeSelectionEvent("EMPTY_SELECTION_AFTER_MOUSEUP", {
          reason: "keep-current-pending-highlight"
        });
      } else {
        console.log("recieved selection", selection);
        // let anchor = selection.anchorOffset;
        // let focus = selection.focusOffset;

        range.value = selection.getRangeAt(0);
        console.log(range.value);
        if (range.value.startContainer.parentNode.id === "essayArea") {
          if (selectionNode.value) {
            removeHighlight("replaced-by-new-selection");
          }

          const fallout = range.value.extractContents();

          selectionNode.value = preserveNewLines(fallout);

          range.value.insertNode(selectionNode.value);
          selection.removeAllRanges();
          justHighlighted = true;
          writeSelectionEvent(
            "SELECTION_CREATED",
            {
            startParentId:
              range.value.startContainer &&
              range.value.startContainer.parentNode &&
              range.value.startContainer.parentNode.id
                ? range.value.startContainer.parentNode.id
                : null,
            justHighlighted
            },
            range.value
          );
        } else {
          writeSelectionEvent("SELECTION_SKIPPED_NON_ROOT", {
            startParentId:
              range.value.startContainer &&
              range.value.startContainer.parentNode &&
              range.value.startContainer.parentNode.id
                ? range.value.startContainer.parentNode.id
                : null
          });
        }
      }
    };

    const handleBack = () => {
      pushRoute("003", { id: feedback.value.prompt_id, module: "Expert" });
    };

    const feedback = computed(() =>
      store.getters.findFeedback({
        id: store.state.uniqueId,
        version: "Expert"
      })
    );
    const exersize = computed(() =>
      ({
        prompt: store.state.prompts.find(p => p.id === feedback.value.prompt_id)
      })
    );
    onMounted(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (!feedback.value.prompt_id) {
        pushRoute("002", { module: "Expert" });
      }
      // Initialize essay text imperatively so Vue never manages #essayArea's
      // children. If Vue owned this content (via {{ }} mustache), any reactive
      // state change (e.g. selectionNode, isSubmitting) would trigger a vdom
      // reconcile that wipes manually-injected label spans immediately.
      if (doc.value) {
        doc.value.textContent =
          (feedback.value.content && feedback.value.content.raw && feedback.value.content.raw.text)
            ? feedback.value.content.raw.text
            : "Text was not submitted";
      }

      installSelectionDebugExports();
      document.addEventListener("selectionchange", handleSelectionChange);
      writeSelectionEvent("DEBUG_SESSION_STARTED", {
        note: "Use window.downloadLabelSelectionLog() to export logs"
      });
    });

    onBeforeUnmount(() => {
      stopSubmittingUi();
      document.removeEventListener("selectionchange", handleSelectionChange);

      if (typeof window !== "undefined") {
        delete window.downloadLabelSelectionLog;
        delete window.clearLabelSelectionLog;
      }
    });

      const items = [
        {
          text: "Essay Writing: LEQ"
        },
        {
          text: "Choose a Prompt"
        },
        {
          text: "LEQ Essay",
          active: "yes!"
        }
      ];

    return {
      doc,
      handleHighlight,
      preserve,
      items,
      exersize,
      feedback,
      checkHighlight,
      handleClickLabel,
      handleClick,
      parseUserSelections,
      handleBack,
      isSubmitting,
      loadingStepMessage,
      showLongWaitMessage,
      authPrompt,
      authPromptVariant,
      submissionError,
      remainingFeedbackAttempts,
      isAuthenticated,
      isProUser,
      selectionNode,
      labelOptions,
      handleFixedLabel,
      goToLogin,
      goToSignup
    };
  }
};
</script>

<style lang="sass" scoped src="@/assets/css/labelSection.sass"></style>
<style lang="sass" scoped src="@/assets/css/essayWriting.sass"></style>
<style scoped>
.top-back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #8c30f5;
  background: transparent;
  font-size: 16px;
  font-weight: 700;
  color: #8c30f5;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;
}

.top-back-link:hover {
  color: #8c30f5;
  border-color: #8c30f5;
  background: rgba(140, 48, 245, 0.08);
  box-shadow: 0 4px 10px rgba(140, 48, 245, 0.2);
  transform: translateY(-1px);
  text-decoration: none;
}

.top-back-link:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(140, 48, 245, 0.16);
}

.top-nav-row {
  margin-bottom: 2px;
}

.breadcrumb-row {
  margin-bottom: 4px;
}

.white-container {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto !important;
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}

.essay-area {
  position: relative;
  margin: 0 auto;
  width: 100%;
  padding: 0 22px !important;
}

.label-content-column {
  width: 100%;
  margin: 0 auto;
}

.fixed-toolbar-shell {
  position: sticky;
  top: 10px;
  z-index: 20;
  margin: 24px auto 0;
  padding: 20px 24px;
  border: 1.5px solid #ddd8ef;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.04);
}

.fixed-toolbar-shell--inactive {
  background: #fbfbfd;
}

.fixed-toolbar-title {
  font-size: 16px;
  font-weight: 600;
}

.fixed-toolbar-description {
  margin: 8px 0 16px 0;
  color: #4b5563;
  font-size: 0.85rem;
  line-height: 1.3;
}

.fixed-toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.fixed-label-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid #d4d4d8;
  border-radius: 999px;
  background: #ffffff;
  color: #1f2937;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.45rem 0.75rem;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.fixed-label-btn:not(:disabled):hover {
  background: #f7f4ff;
  border-color: #bda8e8;
  transform: translateY(-1px);
}

.fixed-label-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.fixed-label-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}

#essayArea {
  white-space: pre-line;
}

.labelled-token {
  border-radius: 5px;
  padding: 0 1px;
}

.feedback-access-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 0.9rem;
  border-radius: 16px;
  border: 1px solid transparent;
}

.feedback-access-banner--guest {
  background: #fff8eb;
  border-color: #f3d9a8;
}

.feedback-access-banner--free {
  background: #f5f3ff;
  border-color: #ddd6fe;
}

.feedback-access-banner--limit {
  background: #fff4f2;
  border-color: #fecaca;
}

.feedback-access-banner--pro {
  background: #ecfdf3;
  border-color: #bbf7d0;
}

.feedback-access-label {
  margin: 0 0 0.3rem;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.feedback-access-message {
  color: #1f2937;
  font-size: 0.98rem;
  font-weight: 600;
  line-height: 1.45;
}

.feedback-access-badge {
  flex-shrink: 0;
  border-radius: 999px;
  padding: 0.4rem 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  color: #4b5563;
  font-size: 0.82rem;
  font-weight: 700;
}

.pending-label {
  background-color: #ffc7c2;
  border: 1px dashed rgba(185, 28, 28, 0.42);
}

.action-buttons-row {
  margin-left: 0 !important;
  margin-right: 0 !important;
  gap: 10px;
  justify-content: flex-end;
}

.action-button {
  min-height: 52px;
  border-radius: 30px;
  padding: 10px;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: 25px;
  letter-spacing: 2px;
}

.action-button-back {
  min-width: 130px;
  max-width: 160px;
}

.action-button-submit {
  max-width: 350px;
}

.submit-note {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 2px;
  max-width: 350px;
}

.attempts-banner-near-submit {
  width: 100%;
  margin-top: 4px;
}

@media (max-width: 576px) {
  .essay-area {
    padding: 0 16px !important;
  }

  .feedback-access-banner {
    flex-direction: column;
    align-items: flex-start;
  }

  .fixed-toolbar-shell {
    top: 6px;
    padding: 0.55rem;
  }

  .fixed-toolbar-title {
    font-size: 0.72rem;
    margin-bottom: 0.2rem;
  }

  .fixed-toolbar-description {
    font-size: 0.8rem;
    margin-bottom: 0.45rem;
  }

  .fixed-toolbar-row {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 2px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .fixed-label-btn {
    flex: 0 0 auto;
    font-size: 0.78rem;
    padding: 0.42rem 0.62rem;
  }

  .action-buttons-row {
    gap: 10px;
  }

  .action-button-back {
    min-width: 110px;
    max-width: 140px;
  }

  .action-button-submit {
    max-width: 280px;
  }
}
</style>
